import React, { useState, useMemo, useEffect } from "react"
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/card"
import { Button } from "../components/button"
import { Progress } from "../components/progress"
import { Check, AlertTriangle, ArrowRight, X, Gauge, Lightbulb, Users, Handshake, Brain, ChevronLeft, ChevronRight, Trophy, Star, CheckCircle2, BarChart3, FileText, RotateCcw } from 'lucide-react'
import TestResultUI from "../components/TestResultUI"
import { cn } from "../lib/utils"
import Navbar from "../components/Navbar"
import Sidebar from "../components/Sidebar"
import { Badge } from '../components/badge';
import { toast } from 'react-toastify';
import api from "../sever/Api"

// Quiz data
const quizCategories = [
  {
    name: "Empathizer",
    description: "Emotionally intelligent, people-centered, nurturing",
    icon: Users,
    color: "from-pink-500 to-rose-600",
    bgColor: "bg-pink-50",
    textColor: "text-pink-700",
    borderColor: "border-pink-200",
    questions: [
      {
        id: "empathizer-q1",
        prompt: "When someone on your team is upset, how do you react?",
        statements: [
          { id: "empathizer-q1-s1", text: "I ask them directly what's wrong.", strength: "Empathizer" },
          { id: "empathizer-q1-s2", text: "I give them time alone.", strength: "Thinker" },
          { id: "empathizer-q1-s3", text: "I push the work forward anyway.", strength: "Deliverer" },
          { id: "empathizer-q1-s4", text: "I try to logically analyze the situation.", strength: "Problem Solver" },
        ],
      },
      {
        id: "empathizer-q2",
        prompt: "I find it easy to:",
        statements: [
          { id: "empathizer-q2-s1", text: "Sense how others feel without them saying it.", strength: "Empathizer" },
          { id: "empathizer-q2-s2", text: "Ignore distractions and focus.", strength: "Deliverer" },
          { id: "empathizer-q2-s3", text: "Connect ideas into big concepts.", strength: "Thinker" },
          { id: "empathizer-q2-s4", text: "Argue from different angles.", strength: "Problem Solver" },
        ],
      },
      {
        id: "empathizer-q3",
        prompt: "People often say I:",
        statements: [
          { id: "empathizer-q3-s1", text: "Am a good listener.", strength: "Empathizer" },
          { id: "empathizer-q3-s2", text: "Am task-oriented.", strength: "Deliverer" },
          { id: "empathizer-q3-s3", text: "Inspire them.", strength: "Coach" },
          { id: "empathizer-q3-s4", text: "Am full of ideas.", strength: "Thinker" },
        ],
      },
      {
        id: "empathizer-q4",
        prompt: "In conversations, I usually:",
        statements: [
          { id: "empathizer-q4-s1", text: "Feel the other person's emotions strongly.", strength: "Empathizer" },
          { id: "empathizer-q4-s2", text: "Think about the logic behind what's said.", strength: "Thinker" },
          { id: "empathizer-q4-s3", text: "Look for how to help the person improve.", strength: "Coach" },
          { id: "empathizer-q4-s4", text: "Focus on keeping things on track.", strength: "Deliverer" },
        ],
      },
      {
        id: "empathizer-q5",
        prompt: "When a teammate is quiet or withdrawn:",
        statements: [
          { id: "empathizer-q5-s1", text: "I gently check in on them.", strength: "Empathizer" },
          { id: "empathizer-q5-s2", text: "I assume they're just thinking.", strength: "Thinker" },
          { id: "empathizer-q5-s3", text: "I encourage them to share.", strength: "Coach" },
          { id: "empathizer-q5-s4", text: "I move forward with the task.", strength: "Deliverer" },
        ],
      },
    ],
  },
  {
    name: "Thinker",
    description: "Curiosity-driven, pattern-seeking, analytical",
    icon: Brain,
    color: "from-blue-500 to-indigo-600",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    borderColor: "border-blue-200",
    questions: [
      {
        id: "thinker-q1",
        prompt: "I enjoy:",
        statements: [
          { id: "thinker-q1-s1", text: "Solving abstract or intellectual challenges.", strength: "Thinker" },
          { id: "thinker-q1-s2", text: "Comforting friends in need.", strength: "Empathizer" },
          { id: "thinker-q1-s3", text: "Coaching others through doubts.", strength: "Coach" },
          { id: "thinker-q1-s4", text: "Crossing tasks off a to-do list.", strength: "Deliverer" },
        ],
      },
      {
        id: "thinker-q2",
        prompt: "When faced with a tough decision:",
        statements: [
          { id: "thinker-q2-s1", text: "I break it down logically.", strength: "Thinker" },
          { id: "thinker-q2-s2", text: "I go with my gut feeling.", strength: "Empathizer" },
          { id: "thinker-q2-s3", text: "I ask someone I trust for advice.", strength: "Coach" },
          { id: "thinker-q2-s4", text: "I weigh risks and choose the most doable option.", strength: "Problem Solver" },
        ],
      },
      {
        id: "thinker-q3",
        prompt: "I often spend time:",
        statements: [
          { id: "thinker-q3-s1", text: "Reading or researching new ideas.", strength: "Thinker" },
          { id: "thinker-q3-s2", text: "Planning and organizing.", strength: "Deliverer" },
          { id: "thinker-q3-s3", text: "Listening to others' experiences.", strength: "Empathizer" },
          { id: "thinker-q3-s4", text: "Testing solutions hands-on.", strength: "Problem Solver" },
        ],
      },
      {
        id: "thinker-q4",
        prompt: "Friends would describe me as:",
        statements: [
          { id: "thinker-q4-s1", text: "Curious and deep.", strength: "Thinker" },
          { id: "thinker-q4-s2", text: "Kind and supportive.", strength: "Empathizer" },
          { id: "thinker-q4-s3", text: "Motivating and driven.", strength: "Coach" },
          { id: "thinker-q4-s4", text: "Efficient and practical.", strength: "Deliverer" },
        ],
      },
      {
        id: "thinker-q5",
        prompt: "If something doesn't make sense, I:",
        statements: [
          { id: "thinker-q5-s1", text: "Ask questions until I understand.", strength: "Thinker" },
          { id: "thinker-q5-s2", text: "Trust it will work out emotionally.", strength: "Empathizer" },
          { id: "thinker-q5-s3", text: "Relate it to someone's story.", strength: "Coach" },
          { id: "thinker-q5-s4", text: "Try different approaches to fix it.", strength: "Problem Solver" },
        ],
      },
    ],
  },
  {
    name: "Coach",
    description: "Motivator, encourager, growth-oriented",
    icon: Handshake,
    color: "from-green-500 to-emerald-600",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    borderColor: "border-green-200",
    questions: [
      {
        id: "coach-q1",
        prompt: "When a friend is stuck, I usually:",
        statements: [
          { id: "coach-q1-s1", text: "Help them believe in their potential.", strength: "Coach" },
          { id: "coach-q1-s2", text: "Give them emotional support.", strength: "Empathizer" },
          { id: "coach-q1-s3", text: "Guide them with a structured plan.", strength: "Deliverer" },
          { id: "coach-q1-s4", text: "Help them think through different strategies.", strength: "Problem Solver" },
        ],
      },
      {
        id: "coach-q2",
        prompt: "I get excited when I can:",
        statements: [
          { id: "coach-q2-s1", text: "Support someone's growth.", strength: "Coach" },
          { id: "coach-q2-s2", text: "Discover a new theory.", strength: "Thinker" },
          { id: "coach-q2-s3", text: "Complete a checklist.", strength: "Deliverer" },
          { id: "coach-q2-s4", text: "Empathize with someone's feelings.", strength: "Empathizer" },
        ],
      },
      {
        id: "coach-q3",
        prompt: "People say I:",
        statements: [
          { id: "coach-q3-s1", text: "Bring out the best in others.", strength: "Coach" },
          { id: "coach-q3-s2", text: "Am logical and analytical.", strength: "Thinker" },
          { id: "coach-q3-s3", text: "Am always dependable.", strength: "Deliverer" },
          { id: "coach-q3-s4", text: "Understand feelings deeply.", strength: "Empathizer" },
        ],
      },
      {
        id: "coach-q4",
        prompt: "I prefer:",
        statements: [
          { id: "coach-q4-s1", text: "Encouraging progress.", strength: "Coach" },
          { id: "coach-q4-s2", text: "Investigating ideas.", strength: "Thinker" },
          { id: "coach-q4-s3", text: "Following steps to reach a goal.", strength: "Deliverer" },
          { id: "coach-q4-s4", text: "Connecting emotionally.", strength: "Empathizer" },
        ],
      },
      {
        id: "coach-q5",
        prompt: "In group settings, I tend to:",
        statements: [
          { id: "coach-q5-s1", text: "Cheer people on.", strength: "Coach" },
          { id: "coach-q5-s2", text: "Lead by emotional example.", strength: "Empathizer" },
          { id: "coach-q5-s3", text: "Handle logistics.", strength: "Deliverer" },
          { id: "coach-q5-s4", text: "Pose questions to spark thought.", strength: "Thinker" },
        ],
      },
    ],
  },
  {
    name: "Problem Solver",
    description: "Solution-focused, strategic, logical",
    icon: Lightbulb,
    color: "from-yellow-500 to-orange-600",
    bgColor: "bg-yellow-50",
    textColor: "text-yellow-700",
    borderColor: "border-yellow-200",
    questions: [
      {
        id: "problem-solver-q1",
        prompt: "When facing obstacles, I:",
        statements: [
          { id: "problem-solver-q1-s1", text: "Break them down into solvable steps.", strength: "Problem Solver" },
          { id: "problem-solver-q1-s2", text: "Look for inspiration in others.", strength: "Coach" },
          { id: "problem-solver-q1-s3", text: "Push forward with the checklist.", strength: "Deliverer" },
          { id: "problem-solver-q1-s4", text: "Pause to understand my emotions.", strength: "Empathizer" },
        ],
      },
      {
        id: "problem-solver-q2",
        prompt: "I enjoy:",
        statements: [
          { id: "problem-solver-q2-s1", text: "Tackling problems others avoid.", strength: "Problem Solver" },
          { id: "problem-solver-q2-s2", text: "Listening to others' feelings.", strength: "Empathizer" },
          { id: "problem-solver-q2-s3", text: "Organizing events or plans.", strength: "Deliverer" },
          { id: "problem-solver-q2-s4", text: "Exploring theories and ideas.", strength: "Thinker" },
        ],
      },
      {
        id: "problem-solver-q3",
        prompt: "People admire my ability to:",
        statements: [
          { id: "problem-solver-q3-s1", text: "Stay calm and find fixes.", strength: "Problem Solver" },
          { id: "problem-solver-q3-s2", text: "Understand their emotional needs.", strength: "Empathizer" },
          { id: "problem-solver-q3-s3", text: "Motivate them to improve.", strength: "Coach" },
          { id: "problem-solver-q3-s4", text: "Think conceptually.", strength: "Thinker" },
        ],
      },
      {
        id: "problem-solver-q4",
        prompt: "When someone shares a problem:",
        statements: [
          { id: "problem-solver-q4-s1", text: "I immediately think of ways to fix it.", strength: "Problem Solver" },
          { id: "problem-solver-q4-s2", text: "I listen and validate their feelings.", strength: "Empathizer" },
          { id: "problem-solver-q4-s3", text: "I relate it to their potential.", strength: "Coach" },
          { id: "problem-solver-q4-s4", text: "I try to see the big picture.", strength: "Thinker" },
        ],
      },
      {
        id: "problem-solver-q5",
        prompt: "What matters most in tough situations is:",
        statements: [
          { id: "problem-solver-q5-s1", text: "Finding the right solution.", strength: "Problem Solver" },
          { id: "problem-solver-q5-s2", text: "Supporting people through it.", strength: "Empathizer" },
          { id: "problem-solver-q5-s3", text: "Staying positive.", strength: "Coach" },
          { id: "problem-solver-q5-s4", text: "Executing a structured plan.", strength: "Deliverer" },
        ],
      },
    ],
  },
  {
    name: "Deliverer",
    description: "Responsible, consistent, task-oriented",
    icon: Gauge,
    color: "from-purple-500 to-violet-600",
    bgColor: "bg-purple-50",
    textColor: "text-purple-700",
    borderColor: "border-purple-200",
    questions: [
      {
        id: "deliverer-q1",
        prompt: "I feel satisfied when:",
        statements: [
          { id: "deliverer-q1-s1", text: "I complete all my tasks.", strength: "Deliverer" },
          { id: "deliverer-q1-s2", text: "I help someone emotionally.", strength: "Empathizer" },
          { id: "deliverer-q1-s3", text: "I spark someone's growth.", strength: "Coach" },
          { id: "deliverer-q1-s4", text: "I figure out something complex.", strength: "Thinker" },
        ],
      },
      {
        id: "deliverer-q2",
        prompt: "I'm known for being:",
        statements: [
          { id: "deliverer-q2-s1", text: "Reliable and organized.", strength: "Deliverer" },
          { id: "deliverer-q2-s2", text: "Insightful and supportive.", strength: "Empathizer" },
          { id: "deliverer-q2-s3", text: "Visionary and reflective.", strength: "Thinker" },
          { id: "deliverer-q2-s4", text: "Encouraging and enthusiastic.", strength: "Coach" },
        ],
      },
      {
        id: "deliverer-q3",
        prompt: "When a deadline approaches:",
        statements: [
          { id: "deliverer-q3-s1", text: "I make sure the job gets done.", strength: "Deliverer" },
          { id: "deliverer-q3-s2", text: "I check in with team morale.", strength: "Empathizer" },
          { id: "deliverer-q3-s3", text: "I reflect on how to improve next time.", strength: "Coach" },
          { id: "deliverer-q3-s4", text: "I look for what could go wrong.", strength: "Problem Solver" },
        ],
      },
      {
        id: "deliverer-q4",
        prompt: "In a team, I naturally:",
        statements: [
          { id: "deliverer-q4-s1", text: "Take charge of execution.", strength: "Deliverer" },
          { id: "deliverer-q4-s2", text: "Help people stay inspired.", strength: "Coach" },
          { id: "deliverer-q4-s3", text: "Explore ideas.", strength: "Thinker" },
          { id: "deliverer-q4-s4", text: "Watch how others feel.", strength: "Empathizer" },
        ],
      },
      {
        id: "deliverer-q5",
        prompt: "If I commit to something:",
        statements: [
          { id: "deliverer-q5-s1", text: "I always follow through.", strength: "Deliverer" },
          { id: "deliverer-q5-s2", text: "I adjust based on how people feel.", strength: "Empathizer" },
          { id: "deliverer-q5-s3", text: "I make sure it aligns with purpose.", strength: "Coach" },
          { id: "deliverer-q5-s4", text: "I strategize the smartest approach.", strength: "Problem Solver" },
        ],
      },
    ],
  },
]

export default function HighStrengthTest() {
  const location = useLocation();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [showResults, setShowResults] = useState(false)
  const [showReport, setShowReport] = useState(false)
  const [resultsSaved, setResultsSaved] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showIntro, setShowIntro] = useState(true);
  const [autoNext, setAutoNext] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const currentCategory = quizCategories[currentSectionIndex]
  const currentQuestion = currentCategory.questions[currentQuestionIndex]
  const totalSections = quizCategories.length

  const handleAnswerChange = (questionId, statementId, value) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: {
        [statementId]: value,
      },
    }));
    setAutoNext(true);
  }

  useEffect(() => {
    if (autoNext) {
      if (!isLastQuestion) {
        handleNext();
      } else {
        setShowResults(true);
      }
      setAutoNext(false);
    }
    // eslint-disable-next-line
  }, [autoNext]);

  const totalQuestions = useMemo(() => {
    return quizCategories.reduce((acc, category) => acc + category.questions.length, 0)
  }, [])

  const answeredQuestionsCount = useMemo(() => {
    let count = 0
    quizCategories.forEach((category) => {
      category.questions.forEach((question) => {
        if (answers[question.id] && Object.values(answers[question.id]).some(value => value === "selected")) {
          count++
        }
      })
    })
    return count
  }, [answers])

  const currentSectionAnsweredCount = useMemo(() => {
    let count = 0
    currentCategory.questions.forEach((question) => {
      if (answers[question.id] && Object.values(answers[question.id]).some(value => value === "selected")) {
        count++
      }
    })
    return count
  }, [answers, currentCategory])

  const sectionScores = useMemo(() => {
    return quizCategories.map((category) => {
      let count = 0
      category.questions.forEach((question) => {
        if (answers[question.id] && Object.values(answers[question.id]).some(value => value === "selected")) {
          count++
        }
      })
      return count
    })
  }, [answers])

  const canGoNext = currentSectionIndex < totalSections - 1 || currentQuestionIndex < currentCategory.questions.length - 1
  const canGoPrevious = currentSectionIndex > 0 || currentQuestionIndex > 0
  const isLastQuestion = currentSectionIndex === totalSections - 1 && currentQuestionIndex === currentCategory.questions.length - 1

  // Compute how many statements across the whole quiz map to each strength
  const maxScoreByStrength = useMemo(() => {
    const counts = {
      Empathizer: 0,
      Thinker: 0,
      Coach: 0,
      'Problem Solver': 0,
      Deliverer: 0,
    }
    quizCategories.forEach(category => {
      category.questions.forEach(q => {
        q.statements.forEach(s => {
          counts[s.strength] = (counts[s.strength] || 0) + 1
        })
      })
    })
    // Max score per statement is 1
    return counts
  }, [])

  // Save results when they are first shown
  useEffect(() => {
    if (showResults) {
      saveTestResult();
    }
  }, [showResults]);

  // Check for showResults URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const showResultsParam = params.get('showResults');
    if (showResultsParam === 'true') {
      setShowResults(true);
    }
  }, [location.search]);

  const overallProgress = (answeredQuestionsCount / totalQuestions) * 100
  const isCurrentQuestionComplete = useMemo(() => {
    if (!answers[currentQuestion.id]) return false;
    return Object.values(answers[currentQuestion.id]).some(value => value === "selected");
  }, [answers, currentQuestion.id])

  const handleNext = () => {
    if (currentQuestionIndex < currentCategory.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1)
    } else if (currentSectionIndex < totalSections - 1) {
      setCurrentSectionIndex(prev => prev + 1)
      setCurrentQuestionIndex(0)
    } else {
      setShowResults(true)
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1)
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1)
      setCurrentQuestionIndex(quizCategories[currentSectionIndex - 1].questions.length - 1)
    }
  }

  const calculateResults = useMemo(() => {
    const scores = {
      Empathizer: 0,
      Thinker: 0,
      Coach: 0,
      "Problem Solver": 0,
      Deliverer: 0,
    }

    quizCategories.forEach((category) => {
      category.questions.forEach((question) => {
        question.statements.forEach((statement) => {
          const selectedOption = answers[question.id]?.[statement.id]
          if (selectedOption === "selected") {
            scores[statement.strength] += 1
          }
        })
      })
    })

    const sortedStrengths = Object.entries(scores)
      .map(([strength, score]) => ({
        strength,
        score,
        category: quizCategories.find(cat => cat.name === strength)
      }))
      .sort((a, b) => b.score - a.score)

    return sortedStrengths
  }, [answers])

  const saveTestResult = async () => {
    try {
      const results = calculateResults;
      const detailedAnswers = [];

      quizCategories.forEach((category) => {
        category.questions.forEach((question) => {
          question.statements.forEach((statement) => {
            const selectedOption = answers[question.id]?.[statement.id];
            if (selectedOption === "selected") {
              detailedAnswers.push({
                questionId: question.id,
                questionText: question.prompt,
                statementId: statement.id,
                statementText: statement.text,
                selectedOptionId: statement.id,
                selectedOptionText: statement.text,
                strength: statement.strength
              });
            }
          });
        });
      });

      // Calculate overall statistics
      const totalScore = results.reduce((sum, result) => sum + result.score, 0);
      const totalMaxScore = results.reduce(
        (sum, result) => sum + maxScoreByStrength[result.strength],
        0
      );
      const overallPercentage = Math.round((totalScore / totalMaxScore) * 100);

      // Convert scores to an object keyed by strength for easier reporting
      const scoresForSave = {};
      results.forEach((result) => {
        scoresForSave[result.strength] = {
          score: result.score,
          maxScore: maxScoreByStrength[result.strength] || 1,
          percentage: Math.round((result.score / (maxScoreByStrength[result.strength] || 1)) * 100),
        };
      });

      // Save to the unified test-results endpoint so detailed report works in the dashboard
      await api.post("/api/test-results", {
        testType: "high5",
        answers: detailedAnswers,
        scores: scoresForSave,
        overallScore: totalScore,
        overallPercentage: overallPercentage,
      });

      toast.success("HIGH5 Strengths Test results saved successfully!");
      setResultsSaved(true);
    } catch (error) {
      console.error("Error saving HIGH5 test result:", error);
      toast.error(
        error.response?.data?.message || "Failed to save test results"
      );
    }
  };


  const getLevel = (percentage) => {
    if (percentage >= 80) return { level: 'Very High', color: 'bg-red-500' }
    if (percentage >= 65) return { level: 'High', color: 'bg-orange-500' }
    if (percentage >= 50) return { level: 'Moderate', color: 'bg-yellow-500' }
    if (percentage >= 35) return { level: 'Low', color: 'bg-blue-500' }
    return { level: 'Very Low', color: 'bg-green-500' }
  }

  // Helper functions for detailed report
  const getHigh5StrengthDescription = (strengthName, percentage) => {
    const strength = quizCategories.find(s => s.name === strengthName);
    if (!strength) return "This strength is not yet fully defined in our system.";

    let description = strength.description;
    if (percentage >= 80) {
      description += " You excel in this area and are likely to be a strong performer in roles requiring this strength.";
    } else if (percentage >= 50) {
      description += " You have a good understanding of this strength but could benefit from specific strategies.";
    } else {
      description += " You might need to work on developing this strength to improve your effectiveness.";
    }
    return description;
  };

  const getStrengthLeverage = (strengthName) => {
    switch (strengthName) {
      case "Empathizer":
        return [
          "Use your emotional intelligence to build strong relationships",
          "Leverage your empathy to understand team dynamics",
          "Apply your nurturing nature to support team members"
        ];
      case "Thinker":
        return [
          "Use your analytical skills to solve complex problems",
          "Leverage your curiosity to explore new ideas",
          "Apply your pattern-seeking abilities to identify trends"
        ];
      case "Deliverer":
        return [
          "Use your task-oriented nature to drive results",
          "Leverage your focus to complete projects on time",
          "Apply your efficiency to optimize processes"
        ];
      case "Coach":
        return [
          "Use your motivational skills to inspire others",
          "Leverage your guidance to develop team members",
          "Apply your encouragement to build confidence"
        ];
      case "Problem Solver":
        return [
          "Use your analytical thinking to identify solutions",
          "Leverage your logical approach to resolve conflicts",
          "Apply your critical thinking to make decisions"
        ];
      default:
        return ["This strength is not yet fully defined in our system."];
    }
  };

  const getStrengthDevelopment = (strengthName) => {
    switch (strengthName) {
      case "Empathizer":
        return [
          "Practice setting boundaries to avoid emotional burnout",
          "Develop assertiveness skills",
          "Learn to balance empathy with objectivity"
        ];
      case "Thinker":
        return [
          "Practice communicating complex ideas simply",
          "Develop emotional intelligence",
          "Learn to balance analysis with action"
        ];
      case "Deliverer":
        return [
          "Practice flexibility and adaptability",
          "Develop relationship-building skills",
          "Learn to balance efficiency with quality"
        ];
      case "Coach":
        return [
          "Practice active listening",
          "Develop technical expertise",
          "Learn to balance guidance with autonomy"
        ];
      case "Problem Solver":
        return [
          "Practice empathy and understanding",
          "Develop collaboration skills",
          "Learn to balance logic with intuition"
        ];
      default:
        return ["This strength is not yet fully defined in our system."];
    }
  };

  const getCareerApplications = (strengthName) => {
    switch (strengthName) {
      case "Empathizer":
        return ["Human Resources", "Counseling", "Social Work", "Customer Service", "Team Leadership"];
      case "Thinker":
        return ["Research", "Analysis", "Strategy", "Consulting", "Innovation"];
      case "Deliverer":
        return ["Project Management", "Operations", "Administration", "Quality Control", "Execution"];
      case "Coach":
        return ["Training", "Mentoring", "Leadership Development", "Education", "Performance Management"];
      case "Problem Solver":
        return ["Engineering", "Consulting", "Troubleshooting", "Risk Management", "Strategic Planning"];
      default:
        return ["This strength is not yet fully defined in our system."];
    }
  };

  const getTeamCollaboration = (strengthName) => {
    switch (strengthName) {
      case "Empathizer":
        return "You excel at building team cohesion and understanding team dynamics. Your ability to sense others' emotions makes you invaluable for team morale and conflict resolution.";
      case "Thinker":
        return "You contribute strategic insights and analytical thinking to team discussions. Your curiosity and pattern-seeking abilities help teams explore new possibilities and solve complex problems.";
      case "Deliverer":
        return "You ensure team projects stay on track and meet deadlines. Your focus and efficiency help teams maintain momentum and achieve results consistently.";
      case "Coach":
        return "You inspire and develop team members through encouragement and guidance. Your motivational skills help teams build confidence and reach their potential.";
      case "Problem Solver":
        return "You approach team challenges with logical analysis and critical thinking. Your problem-solving abilities help teams overcome obstacles and make informed decisions.";
      default:
        return "This strength is not yet fully defined in our system.";
    }
  };

  const getPersonalDevelopment = (strengthName) => {
    switch (strengthName) {
      case "Empathizer":
        return "Focus on developing emotional boundaries and assertiveness. Practice balancing your natural empathy with self-care and objective decision-making. Consider roles that allow you to use your emotional intelligence while maintaining healthy boundaries.";
      case "Thinker":
        return "Work on communicating your ideas clearly and developing emotional intelligence. Practice balancing your analytical thinking with action and collaboration. Consider roles that allow you to use your intellectual strengths while developing interpersonal skills.";
      case "Deliverer":
        return "Focus on developing flexibility and relationship-building skills. Practice balancing your efficiency with quality and adaptability. Consider roles that allow you to use your execution skills while developing strategic thinking.";
      case "Coach":
        return "Work on developing technical expertise and active listening skills. Practice balancing your guidance with autonomy and collaboration. Consider roles that allow you to use your motivational skills while developing subject matter expertise.";
      case "Problem Solver":
        return "Focus on developing empathy and collaboration skills. Practice balancing your logical approach with understanding and teamwork. Consider roles that allow you to use your problem-solving abilities while developing emotional intelligence.";
      default:
        return "This strength is not yet fully defined in our system.";
    }
  };

  if (showIntro) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        {/* Sidebar */}
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>

        {/* Main content */}
        <div className="ml-64">
          <Navbar />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <Card className="text-center bg-white shadow-lg">
                <CardHeader className="space-y-4">
                  <CardTitle className="text-3xl font-bold">HIGH5 Strengths Test</CardTitle>
                  <CardDescription className="text-lg text-gray-600">
                    Discover your unique strengths and working style
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Strengths Overview */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    <div className="space-y-2">
                      <h3 className="font-semibold text-pink-600">Empathizer</h3>
                      <p className="text-sm text-gray-600">Emotional intelligence & relationship building</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-indigo-600">Thinker</h3>
                      <p className="text-sm text-gray-600">Curiosity & analytical mindset</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-green-600">Coach</h3>
                      <p className="text-sm text-gray-600">Motivation & team guidance</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-yellow-600">Problem Solver</h3>
                      <p className="text-sm text-gray-600">Logical approach & critical thinking</p>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-blue-600">Deliverer</h3>
                      <p className="text-sm text-gray-600">Execution & task completion</p>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>{quizCategories.reduce((acc, cat) => acc + cat.questions.length, 0)} questions</strong> •
                      Gain insights into your strengths & career fit
                    </p>
                  </div>

                  <Button
                    onClick={() => setShowIntro(false)}
                    className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                    size="lg"
                  >
                    Start Test <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (showResults) {
    // Build AI report data
    const aiScores = (() => {
      const map = {};
      calculateResults.forEach((r) => {
        const maxPos = maxScoreByStrength[r.strength] || 1;
        map[r.strength] = {
          score: r.score,
          maxScore: maxPos,
          percentage: Math.round((r.score / maxPos) * 100)
        };
      });
      return map;
    })();

    const aiAnswers = (() => {
      const list = [];
      quizCategories.forEach((category) => {
        category.questions.forEach((question) => {
          question.statements.forEach((statement) => {
            const selectedOption = answers[question.id]?.[statement.id];
            if (selectedOption === 'selected') {
              list.push({
                questionId: question.id,
                questionText: question.prompt,
                selectedOptionId: statement.id,
                selectedOptionText: statement.text
              });
            }
          });
        });
      });
      return list;
    })();

    const totalScoreForAI = calculateResults.reduce((sum, r) => sum + r.score, 0);
    const totalMaxForAI = calculateResults.reduce((sum, r) => sum + (maxScoreByStrength[r.strength] || 1), 0);
    const overallPercentageForAI = Math.round((totalScoreForAI / totalMaxForAI) * 100);

    const aiReportData = {
      testType: 'high5',
      scores: aiScores,
      answers: aiAnswers,
      overallScore: totalScoreForAI,
      overallPercentage: overallPercentageForAI
    };

    const resultsContent = (
      <div className="space-y-6">
        {calculateResults.map((result) => {
          const strengthName = result.strength
          const maxPos = maxScoreByStrength[strengthName] || 1
          const percentage = Math.round((result.score / maxPos) * 100)
          const { level, color } = getLevel(percentage)
          return (
            <div key={strengthName} className="space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">{strengthName}</h3>
                <div className="text-right">
                  <span className="font-bold">{result.score}/{maxPos}</span>
                  <span className={`ml-2 px-2 py-1 rounded text-xs text-white ${color}`}>{level}</span>
                </div>
              </div>
              <Progress value={percentage} className="h-3" />
              <p className="text-sm text-gray-600">{result.category?.description}</p>
            </div>
          )
        })}
      </div>
    )

    const detailedReportContent = (
      <>
        {calculateResults.map((result) => {
          const strengthName = result.strength
          const maxPos = maxScoreByStrength[strengthName] || 1
          const percentage = Math.round((result.score / maxPos) * 100)
          const { level, color } = getLevel(percentage)
          return (
            <Card className='bg-[#ffffff]' key={strengthName}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl">{strengthName}</CardTitle>
                    <CardDescription>{result.category?.description}</CardDescription>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{result.score}/{maxPos}</div>
                    <span className={`px-3 py-1 rounded-full text-sm text-white ${color}`}>{level}</span>
                  </div>
                </div>
                <Progress value={percentage} className="h-2" />
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Enhanced Content */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Your Strength Profile</h4>
                  <p className="text-gray-700">{getHigh5StrengthDescription(strengthName, percentage)}</p>
                </div>

                {/* Key Characteristics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2">How to Leverage This Strength</h4>
                    <ul className="space-y-1">
                      {getStrengthLeverage(strengthName).map((leverage, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-green-700 text-sm">{leverage}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-800 mb-2">Development Areas</h4>
                    <ul className="space-y-1">
                      {getStrengthDevelopment(strengthName).map((area, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <Lightbulb className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span className="text-orange-700 text-sm">{area}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Career & Teamwork */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Career Applications</h4>
                    <div className="flex flex-wrap gap-2">
                      {getCareerApplications(strengthName).map((career, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {career}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 mb-2">Team Collaboration</h4>
                    <p className="text-purple-700 text-sm leading-relaxed">{getTeamCollaboration(strengthName)}</p>
                  </div>
                </div>

                {/* Personal Development */}
                <div className="bg-emerald-50 rounded-lg p-4">
                  <h4 className="font-semibold text-emerald-800 mb-2">Personal Development</h4>
                  <p className="text-emerald-700 text-sm leading-relaxed">{getPersonalDevelopment(strengthName)}</p>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </>
    )

    return (
      <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        </div>
        <div className="ml-0 md:ml-64 min-h-screen">
          <Navbar toggleSidebar={toggleSidebar} />
          <TestResultUI
            testTitle="HIGH5 Strengths"
            testDescription="Based on the HIGH5 strengths model"
            results={resultsContent}
            aiReportData={aiReportData}
            onRetake={() => {
              setShowResults(false)
              setShowReport(false)
              setCurrentSectionIndex(0)
              setCurrentQuestionIndex(0)
              setAnswers({})
              setResultsSaved(false)
            }}
            onBack={() => { }}
            showBackButton={false}
          >
            {detailedReportContent}
          </TestResultUI>
        </div>
      </div>
    )
  }


  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ background: 'linear-gradient(90deg, #FFFFFF 0%, #EFF6FF 100%, #F9FAFB 100%)' }}>
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>
      <div className="ml-0 md:ml-64 min-h-screen " >
        <Navbar
          toggleSidebar={toggleSidebar}
        />
        <div className=" mx-auto" style={{ width: '85%' }}>
          {/* Header */}
          <div className="text-center mb-8 mt-8">

            <h1 className="text-4xl font-bold text-gray-900 mb-2">HIGH5 Strengths Test</h1>
            <p className="text-xl text-gray-600">Discover your unique strengths and working style</p>
          </div>

          {/* Progress by Section */}
          <Card className="rounded-lg border text-card-foreground shadow-sm mb-6 bg-[#ffffff] ">
            <CardHeader>
              <CardTitle className="text-lg">Your Progress by Section</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4 mb-6">
                {quizCategories.map((section, index) => {
                  const Icon = section.icon
                  return (
                    <div key={section.name} className="text-center">
                      <div className={`w-12 h-12 mx-auto mb-2 rounded-full ${section.bgColor} flex items-center justify-center 
                        }`}>
                        <Icon className={`w-6 h-6 ${section.textColor}`} />
                      </div>
                      <h3 className="font-medium text-sm mb-1">{section.name}</h3>
                      <p className="text-xs text-gray-500">{sectionScores[index]}/5</p>
                    </div>
                  )
                })}
              </div>


            </CardContent>
          </Card>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600">
                Question {currentSectionIndex * 5 + currentQuestionIndex + 1} of {totalQuestions}
              </span>
              <span className="text-sm text-gray-600">
                {Math.round(overallProgress)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>


          {/* Question Card */}
          <Card className="rounded-lg border text-card-foreground shadow-sm bg-[#ffffff]">
            <CardHeader className="pb-0 border-b">
              <div className="flex items-start gap-4">
                {/* <div className={`w-12 h-12 rounded-full ${currentCategory.bgColor} flex items-center justify-center flex-shrink-0`}>
                  <currentCategory.icon className={`w-6 h-6 ${currentCategory.textColor}`} />
                </div> */}
                <div className="flex-1">
                  <CardTitle className="text-xl mb-2">
                    {currentSectionIndex + 1}.{currentQuestionIndex + 1} {currentCategory.name}
                  </CardTitle>
                  <p className="text-sm text-gray-500 mb-4">{currentCategory.description}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <h3 className="text-xl font-semibold mb-6 text-gray-900 leading-relaxed mt-3">
                {currentQuestion.prompt}
              </h3>



              <div className="space-y-3">
                {currentQuestion.statements.map((statement, index) => {
                  const isSelected = answers[currentQuestion.id]?.[statement.id] === "selected"
                  return (
                    <div
                      key={statement.id}
                      onClick={() => handleAnswerChange(currentQuestion.id, statement.id, "selected")}
                      className={`flex items-center rounded-xl border-2 p-3 px-4 cursor-pointer transition-all duration-200 ${isSelected
                        ? "border-primary ring-2 ring-primary/50 bg-primary/5 dark:bg-primary/10"
                        : "hover:bg-[rgb(197,199,203)] dark:hover:bg-gray-800dark:hover:bg-gray-800"
                        }`}
                    >
                      <div className="flex items-center gap-4 w-full">
                        <div className="flex-1">
                          <div className="">{statement.text}</div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between items-center m-5">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              className="bg-[#ffffff] px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none border border-gray-300 text-gray-700 bg-white hover:bg-gray-100"
            >
              Previous
            </Button>


            {/* <Button
              onClick={handleNext}
              disabled={!isCurrentQuestionComplete}
              className={`bg-purple-600 hover:bg-purple-700 text-white flex items-center space-x-2`}
            >
              {isLastQuestion ? "View Results" : canGoNext ? "Next Question" : "Next Section"}

            </Button> */}
          </div>
        </div>
      </div>
    </div>
  )
} 